import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
} from "@mui/material";
import { apiService } from "../app/api";
import BaseLayout from "../layout/BaseLayout";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MultiSelectDropdown from "../component/MultiSelectDropdown";
import { toast } from "react-toastify";
import { handleAxiosError } from "../helper/errorHandler";
import { formatTimestamp } from "../helper/formatTimestamp";

interface SttModelLanguage {
  _id: string;
  model: string;
  supported_languages: string[];
  createdAt: string;
}

const MODEL_OPTIONS = ["narris_core", "narris_edge", "narris_fast"];

const SttModelLanguage: React.FC = () => {
  const [models, setModels] = useState<SttModelLanguage[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newModel, setNewModel] = useState("");
  const [newLanguages, setNewLanguages] = useState<string[]>([]);

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editModel, setEditModel] = useState("");
  const [editLanguages, setEditLanguages] = useState<string[]>([]);

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Fetch STT models
  const fetchModels = async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get(`/stt-languages`);
      setModels(data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch STT Models.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const { data } = await apiService.get(
        "/languages?is_active=true&page=1&limit=200&fields=language"
      );
      const langArray = data.data.map((lang: any) => lang.language);
      setLanguages(langArray);
    } catch (error: any) {
      handleAxiosError(error);
    }
  };

  useEffect(() => {
    fetchModels();
    fetchLanguages();
  }, []);

  const handleAdd = async () => {
    if (!newModel) {
      toast.warning("Please select a model.");
      return;
    }
    if (newLanguages.length === 0) {
      toast.warning("Please select at least one supported language.");
      return;
    }

    const payload = {
      model: newModel,
      supported_languages: newLanguages,
    };

    try {
      const { data } = await apiService.post("/stt-language", payload);
      toast.success(data.message);
      setAddDialogOpen(false);
      setNewModel("");
      setNewLanguages([]);
      fetchModels();
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  // EDIT HANDLER
  const handleEditClick = (item: SttModelLanguage) => {
    setEditId(item._id);
    setEditModel(item.model);
    setEditLanguages(item.supported_languages);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (editLanguages.length === 0) {
      toast.warning("Please select at least one supported language.");
      return;
    }

    const payload = {
      model: editModel,
      supported_languages: editLanguages,
    };

    try {
      await apiService.put(`/stt-language/${editId}`, payload);
      toast.success("STT Model updated successfully.");
      setEditDialogOpen(false);
      fetchModels();
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  // DELETE HANDLER
  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await apiService.delete(`/stt-language/${deleteId}`);
      toast.success("STT Model deleted.");
      setDeleteDialogOpen(false);
      fetchModels();
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  return (
    <BaseLayout>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
      >
        <Typography fontSize={30} fontWeight={700}>
          STT Models ({models.length})
        </Typography>

        <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
          Add STT Model
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          {models.length > 0 ? (
            <Grid container spacing={3} mt={3}>
              {models.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item._id}>
                  <Card sx={{ position: "relative", p: 1 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600}>
                        {item.model}
                      </Typography>

                      <Typography variant="body2" color="textSecondary">
                        Added on: {formatTimestamp(item.createdAt)}
                      </Typography>

                      <Box mt={1}>
                        <Typography fontWeight={600}>
                          Supported Languages:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                          {item.supported_languages.map((lang, i) => (
                            <Chip key={i} label={lang} size="small" />
                          ))}
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      {/* <IconButton
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => {
                          setDeleteId(item._id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon color="error" />
                      </IconButton> */}

                      <IconButton
                        sx={{ position: "absolute", top: 8, right: 45 }}
                        onClick={() => handleEditClick(item)}
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography textAlign="center" mt={5}>
              No STT Models found.
            </Typography>
          )}
        </>
      )}

      {/* ADD DIALOG */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New STT Model</DialogTitle>
        <DialogContent>
          <Typography fontWeight={600} mt={1}>
            Select Model Type
          </Typography>

          <select
            style={{ width: "100%", padding: "10px", marginBottom: "20px" }}
            value={newModel}
            onChange={(e) => setNewModel(e.target.value)}
          >
            <option value="">Select model</option>
            {MODEL_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <MultiSelectDropdown
            options={languages}
            value={newLanguages}
            onChange={(v) => setNewLanguages(v)}
            label="Supported Languages"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit STT Model</DialogTitle>
        <DialogContent>
          <TextField fullWidth disabled value={editModel} sx={{ mb: 2 }} />

          <MultiSelectDropdown
            options={languages}
            value={editLanguages}
            onChange={(v) => setEditLanguages(v)}
            label="Supported Languages"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete STT Model</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this STT Model?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </BaseLayout>
  );
};

export default SttModelLanguage;
